import React, { createContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

/**
 * ThemeCustomization Context
 * Manages theme configuration state across the application
 */

export const ThemeCustomizationContext = createContext({
  customization: {
    borderRadius: 12,
    navType: 'light', // 'light' or 'dark'
  },
  updateCustomization: () => {},
});

/**
 * ThemeCustomization Provider
 * Wraps the application to provide theme configuration management
 */
export const ThemeCustomizationProvider = ({ children }) => {
  const [customization, setCustomization] = useState({
    borderRadius: 12,
    navType: 'light',
  });

  const updateCustomization = (updates) => {
    setCustomization((prev) => ({ ...prev, ...updates }));
  };

  const contextValue = useMemo(
    () => ({
      customization,
      updateCustomization,
    }),
    [customization]
  );

  return (
    <ThemeCustomizationContext.Provider value={contextValue}>
      {children}
    </ThemeCustomizationContext.Provider>
  );
};

ThemeCustomizationProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Custom hook to access theme customization
 * @returns {Object} Theme customization context
 */
export const useThemeCustomization = () => {
  const context = React.useContext(ThemeCustomizationContext);
  if (!context) {
    throw new Error('useThemeCustomization must be used within ThemeCustomizationProvider');
  }
  return context;
};

export default ThemeCustomizationProvider;
